'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/global.module.css';
import type { ChartId, ChartInfo } from '../../types/commontypes';
import { getResizeBars } from '@/utils/chartHandlers';
import { useChartUIContext } from '../../contexts/ChartUIContext';
import { useChartUIStoreSelector } from '../../stores/chartUIStore';

export interface ResizeBarConfig {
  topId: ChartId;
  bottomId: ChartId;
  top: number;
}

interface ChartResizerProps {
  width?: number;
  margin: { top: number; bottom: number };
}

export const ChartResizer: React.FC<ChartResizerProps> = ({ width, margin }) => {
  const { chartUIStore } = useChartUIContext();
  const chartInfos = useChartUIStoreSelector(chartUIStore, s => s.chartInfos);
  const setChartInfos = useChartUIStoreSelector(chartUIStore, s => s.setChartInfos);

  // Build chartHeightMap from chartInfos
  const chartHeightMap = useMemo(() => {
    const map: Record<ChartId, number> = {};
    chartInfos.forEach((ch: ChartInfo) => {
      map[ch.chartId] = ch.height;
    });
    return map;
  }, [chartInfos]);
  const [isDragging, setIsDragging] = useState(false);
  const [activeBar, setActiveBar] = useState<ResizeBarConfig | null>(null);
  const dragStartY = useRef(0);
  const isDraggingRef = useRef(false);

  const bars = useMemo(() => {
    const bars = getResizeBars(chartInfos, margin);
    return bars;
  }, [chartInfos, margin]);

  const startDragging = (e: React.MouseEvent, bar: ResizeBarConfig) => {
    // e.preventDefault();
    dragStartY.current = e.clientY;
    setIsDragging(true);
    isDraggingRef.current = true;
    setActiveBar(bar);
  };

  const stopDragging = () => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setActiveBar(null);
  };

  const rafRef = useRef<number | null>(null);
  const pendingDeltaRef = useRef(0);

  const onDragging = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current || !activeBar) return;

      const deltaY = e.clientY - dragStartY.current;
      if (deltaY === 0) return;
      dragStartY.current = e.clientY;
      pendingDeltaRef.current += deltaY;

      if (rafRef.current !== null) return; // rAF 대기 중이면 delta만 누적
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const totalDelta = pendingDeltaRef.current;
        pendingDeltaRef.current = 0;
        if (totalDelta === 0) return;

        const { topId, bottomId } = activeBar;
        const topCurrent = chartHeightMap[topId];
        const bottomCurrent = chartHeightMap[bottomId];

        const newTop = topCurrent + totalDelta;
        const newBottom = bottomCurrent - totalDelta;

        if (newTop < 60 || newBottom < 60) return;

        const newHeights = chartInfos.map((ch: ChartInfo) => {
          if (ch.chartId === topId) return { ...ch, height: newTop };
          if (ch.chartId === bottomId) return { ...ch, height: newBottom };
          return ch;
        });

        setChartInfos(newHeights);
      });
    },
    [activeBar, chartHeightMap, chartInfos, setChartInfos],
  );

  // rAF cleanup
  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', onDragging);
      window.addEventListener('mouseup', stopDragging);
    }
    return () => {
      window.removeEventListener('mousemove', onDragging);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging, onDragging]);

  return (
    <>
      {bars.map((bar, i) => {
        const top = bar.top;
        const barWidth = width ? width - 69 : '91.5%';
        const isActive = isDragging && activeBar?.topId === bar.topId;
        return (
          <div
            key={i}
            className={`${styles['resize_line']} ${isActive ? styles.dragging : ''}`}
            style={{ top, width: barWidth }}
            onMouseDown={e => startDragging(e, bar)}
          />
        );
      })}
    </>
  );
};

export default ChartResizer;
